import { z } from 'zod';
import { idListQuery, idParamSchema, queryBoolean } from './common';
import { printOrientationSchema, printPageSizeSchema } from './print-options';

/**
 * What one press of Print may change about a template, without editing it.
 *
 * The split matters. A *template* is a lab-wide decision, made once and shared;
 * a *job* is "this sheet, now" — the receptionist who needs it on A5 because
 * that is what the tray holds, or without the letterhead because they are
 * printing onto pre-printed stationery. Making those the same control would
 * mean every such print silently rewrote the lab's template.
 *
 * Every field is optional and every omitted field means "as the template says",
 * so a bare `GET /print/patients/12` is the ordinary case.
 */
export const printJobQuerySchema = z.object({
  template_id: idParamSchema.optional(),
  /** Restrict the sheet to some of the patient's reports. Absent means all. */
  report_ids: idListQuery.optional(),
  header: queryBoolean.optional(),
  footer: queryBoolean.optional(),
  watermark: queryBoolean.optional(),
  /**
   * Suppress the signatories at the foot of every page.
   *
   * For printing onto stationery that is already signed, or onto a sheet that
   * will be signed by hand. Like every other flag here it can only turn
   * something *off* or on — it cannot name a signatory. A query string must
   * never be able to forge attribution on a clinical document, which is the
   * same reason `applyJobOverrides` is a shallow per-section merge.
   */
  signatures: queryBoolean.optional(),
  /**
   * Print previous results beside the current ones (requirement 7).
   *
   * A layout flag like the others: it can only turn the template's history
   * columns on or off. **Which** visits appear is `history_ids`, below, and that
   * is data selection rather than layout — see the note there.
   */
  history: queryBoolean.optional(),
  /**
   * Which earlier visits to print (requirement 8).
   *
   * The security rule, which belongs beside the field rather than in a service:
   * **the server never fetches a report by an id taken from here.** It loads the
   * patient's own history itself and uses these ids only to *filter* that
   * already-authorised set — the same rule `report_ids` follows. An id belonging
   * to another patient cannot appear in the fetched set, so it is silently
   * dropped rather than 403'd; there is nothing to leak either way.
   *
   * Capped, unlike `report_ids`, which has no ceiling today: a printed table is
   * four history columns wide at most, and twenty-four ids is already far more
   * than any sheet can render.
   */
  history_ids: idListQuery.pipe(z.array(z.number()).max(24)).optional(),
  page_size: printPageSizeSchema.optional(),
  orientation: printOrientationSchema.optional(),
  /** Sends `Content-Disposition: attachment`, so the browser saves rather than shows. */
  download: queryBoolean.optional(),
});
export type PrintJobQuery = z.infer<typeof printJobQuerySchema>;

/**
 * The registration slip.
 *
 * Two options and no template. A slip has one shape — see the doc comment on
 * `documents/letterhead.ts` — and the only two things a lab actually varies are
 * the paper in the tray and whether the patient's copy shows what they were
 * charged.
 */
/**
 * The papers a slip prints on.
 *
 * Not `printPageSizeSchema`, which is the template builder's paper dropdown and
 * is backed by a fixed width/height pair. A thermal roll has a width and no
 * height, and it has no business being offered as a paper for a clinical report.
 * See `SlipSize` in shared/print-geometry.ts.
 */
export const slipSizeSchema = z.enum(['A4', 'A5', 'THERMAL_80']);
export type SlipSize = z.infer<typeof slipSizeSchema>;

export const SLIP_SIZE_LABELS = {
  A4: 'A4 sheet',
  A5: 'A5 sheet',
  THERMAL_80: '80 mm roll',
} as const satisfies Record<SlipSize, string>;

export const slipQuerySchema = z.object({
  size: slipSizeSchema.default('A5'),
  /**
   * Print the rates and totals.
   *
   * On by default: the slip is what the patient is handed at the desk, and a
   * charge sheet without charges is the reason people come back to ask. Off is
   * for the copy that goes with the sample to the bench.
   */
  charges: queryBoolean.default(true),
  download: queryBoolean.optional(),
});
export type SlipQuery = z.infer<typeof slipQuerySchema>;
